import React from 'react';
import { Link } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';

const Features: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Layout>
      <div className="bg-gradient-to-br from-dark to-dark-dark text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold mb-4 text-primary">Control Your Experience</h1>
            <p className="text-xl max-w-3xl mx-auto">
              Take command of your interactive experiences with Wheels and ATMs. Upgrade to unlock
              the full potential of control, tracking, and advanced features.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
            {/* Guest User */}
            <div className="bg-dark-light rounded-lg overflow-hidden shadow-lg transition-transform hover:scale-105">
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-2xl font-bold mb-2">Basic Experience</h2>
                <p className="text-gray-300">
                  For those just testing the waters, with full access to core features but limited
                  tracking and storage.
                </p>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-primary mb-3">🎯 Wheel Features</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Create unlimited wheels
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Basic wheel themes and customization
                    </li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-primary mb-3">🏧 ATM Features</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Create unlimited ATMs
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Multiple ATM themes and PIN protection
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Transaction history and receipts
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-400 mb-3">⚠️ Limitations</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-500">
                      <svg
                        className="h-4 w-4 text-gray-500 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Local device storage only
                    </li>
                    <li className="flex items-center text-gray-500">
                      <svg
                        className="h-4 w-4 text-gray-500 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Data lost when browser data cleared
                    </li>
                    <li className="flex items-center text-gray-500">
                      <svg
                        className="h-4 w-4 text-gray-500 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      No cross-device sync or sharing controls
                    </li>
                  </ul>
                </div>
                <div className="mt-8 space-y-3">
                  <Link
                    to="/create-wheel"
                    className="block w-full py-2 px-4 text-center bg-primary hover:bg-primary-dark text-white font-bold rounded transition-colors"
                  >
                    Create a Wheel
                  </Link>
                  <Link
                    to="/create-atm"
                    className="block w-full py-2 px-4 text-center bg-blue-600 hover:bg-blue-700 text-white font-bold rounded transition-colors"
                  >
                    Create an ATM
                  </Link>
                </div>
              </div>
            </div>

            {/* Signed In User */}
            <div className="bg-dark-light rounded-lg overflow-hidden shadow-lg transform scale-105 border-2 border-primary">
              <div className="p-6 border-b border-gray-700 bg-primary bg-opacity-20">
                <div className="absolute top-0 right-0 bg-primary text-white px-3 py-1 rounded-bl-lg font-bold text-sm">
                  PREMIUM ACCESS
                </div>
                <h2 className="text-2xl font-bold mb-2">Power Controller</h2>
                <p className="text-gray-300">
                  Take complete control with cloud storage, advanced sharing, analytics, and premium
                  features for both Wheels and ATMs.
                </p>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-primary mb-3">
                    🎯 Premium Wheel Features
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Cloud storage with cross-device sync
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Advanced sharing with usage tracking
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Spin limit controls and result verification
                    </li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-primary mb-3">
                    🏧 Premium ATM Features
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Cloud-synced ATMs with backup protection
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Advanced sharing controls and analytics
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Transaction monitoring and usage limits
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Public ATM directory and discovery
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primary mb-3">
                    ✨ Universal Premium Benefits
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Access from any device, anywhere
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Never lose your creations again
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-4 w-4 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Complete usage analytics and insights
                    </li>
                  </ul>
                </div>
                <div className="mt-8">
                  {isAuthenticated ? (
                    <div className="space-y-3">
                      <Link
                        to="/create-wheel"
                        className="block w-full py-2 px-4 text-center bg-primary hover:bg-primary-dark text-white font-bold rounded transition-colors"
                      >
                        Create Premium Wheel
                      </Link>
                      <Link
                        to="/create-atm"
                        className="block w-full py-2 px-4 text-center bg-blue-600 hover:bg-blue-700 text-white font-bold rounded transition-colors"
                      >
                        Create Premium ATM
                      </Link>
                    </div>
                  ) : (
                    <Link
                      to="/sign-up"
                      className="block w-full py-3 px-4 text-center bg-primary hover:bg-primary-dark text-white font-bold rounded transition-colors"
                    >
                      Unlock Full Control
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Features;
